// client/src/components/AddOrderModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { getAuthHeaders } from '../utils/authHelpers';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;

const COLORS = ['Burgundy', 'Hemp Beige', 'Onyx Black', 'Royal Blue', 'Forest Green', 'Sea Gull Grey', 'Buckskin Tan', 'Aquamarine', 'Caribbean Blue', 'Chocolate Brown', 'Teal', 'Turquoise', 'Harbor Blue', 'Navy Blue', 'Charcoal', 'Arctic White', 'Birch', 'Sand', 'Taupe', 'Sunset Red', 'Vision - Onyx Black', 'Vision - Sea Gull Gray', 'Vision - Hemp Beige', 'Vision - Arctic White', 'Black Mesh', 'N/A'];
const FRAME_TYPES = ['Pontoon', 'Runabout', 'PWC', 'Golf Cart'];
const SHIPPING_METHODS = ['Postal Service', 'LTL', 'Piggyback', 'Pickup'];

function AddOrderModal({ show, handleClose, refreshOrders, setSubmissionMessage, initialOrderType }) {
  const [formData, setFormData] = useState({
    orderType: initialOrderType || '',
    dealerId: '',
    dealerName: '',
    customerName: '',
    customWork: '',
    frameType: '',
    length: '',
    width: '',
    height: '',
    upperColor: '',
    lowerColor: '',
    brackets: '',
    revenue: '',
    orderDate: '',
    shippingMethod: '',
    invoiceNumber: '',
    tbcNumber: ''
  });

  const [piggybackOption, setPiggybackOption] = useState('next');
  const [piggybackOrderName, setPiggybackOrderName] = useState('');
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [modalMessage, setModalMessage] = useState('');
  const [modalMessageType, setModalMessageType] = useState('danger');
  const [loading, setLoading] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [fetchError, setFetchError] = useState('');

  const isParts = formData.orderType === 'Parts';

  const resetState = useCallback(() => {
    setFormData({
      orderType: initialOrderType || '',
      dealerId: '',
      dealerName: '',
      customerName: '',
      customWork: '',
      frameType: '',
      length: '',
      width: '',
      height: '',
      upperColor: '',
      lowerColor: '',
      brackets: '',
      revenue: '',
      orderDate: '',
      shippingMethod: '',
      invoiceNumber: '',
      tbcNumber: ''
    });
    setPiggybackOption('next');
    setPiggybackOrderName('');
    setFetchError('');
    setModalMessage('');
    setModalMessageType('danger');
    setTouched({});
    setErrors({});
  }, [initialOrderType]);

  const onHide = () => {
    resetState();
    handleClose();
  };

  useEffect(() => {
    if (show && initialOrderType && formData.orderType !== initialOrderType) {
      setFormData(prev => ({ ...prev, orderType: initialOrderType }));
    }
  }, [initialOrderType, formData.orderType, show]);

  useEffect(() => {
    const fetchDealers = async () => {
      setFetchError('');
      try {
        const res = await fetch(`${API_ROOT_URL}/dealers`, {
          headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        data.sort((a, b) => a.dealerName.localeCompare(b.dealerName));
        setDealers(data);
      } catch (err) {
        console.error('Error fetching dealer data:', err);
        setFetchError('Failed to load dealer options. Please refresh.');
      }
    };

    if (show) {
      setModalMessage('');
      fetchDealers();
    } else {
      resetState();
    }
  }, [show, resetState]);

  const setFieldTouched = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const buildErrors = (data, pigOpt, pigName) => {
    const e = {};
    if (!data.shippingMethod) e.shippingMethod = 'Shipping Method is required.';
    if (!data.dealerId) e.dealerId = 'Please select a dealer.';
    if (!data.customerName || !data.customerName.trim()) e.customerName = 'Customer Name is required.';
    if (data.invoiceNumber && !data.invoiceNumber.trim()) {
      e.invoiceNumber = 'Invoice number cannot be just whitespace.';
    }
    if (data.tbcNumber && !data.tbcNumber.trim()) {
      e.tbcNumber = 'TBC Number cannot be just whitespace.';
    }
    if (data.shippingMethod === 'Piggyback' && pigOpt === 'custom') {
      if (!pigName || !pigName.trim()) {
        e.piggybackOrderName = 'Enter the specific order name for piggyback.';
      } else if (pigName.trim().length < 2) {
        e.piggybackOrderName = 'Piggyback order name must be at least 2 characters.';
      }
    }

    if (!isParts) {
      if (!data.frameType) e.frameType = 'Frame Type is required.';
      const toNum = (v) => (v === '' ? NaN : Number(v));
      const len = toNum(data.length);
      const wid = toNum(data.width);
      const hei = toNum(data.height);
      const rev = toNum(data.revenue);
      if (!data.length && data.length !== 0) e.length = 'Length is required.';
      if (!data.width && data.width !== 0) e.width = 'Width is required.';
      if (!data.height && data.height !== 0) e.height = 'Height is required.';
      if (!data.upperColor) e.upperColor = 'Upper Color is required.';
      if (!data.lowerColor) e.lowerColor = 'Lower Color is required.';
      if (!data.revenue && data.revenue !== 0) e.revenue = 'Revenue is required.';
      if (data.length !== '' && (Number.isNaN(len) || len < 0)) e.length = 'Length must be a positive number.';
      if (data.width !== '' && (Number.isNaN(wid) || wid < 0)) e.width = 'Width must be a positive number.';
      if (data.height !== '' && (Number.isNaN(hei) || hei < 0)) e.height = 'Height must be a positive number.';
      if (data.revenue !== '' && (Number.isNaN(rev) || rev < 0)) e.revenue = 'Revenue must be a positive amount.';
    } else {
      const toNum = (v) => (v === '' ? NaN : Number(v));
      const rev = toNum(data.revenue);
      if (data.revenue !== '' && (Number.isNaN(rev) || rev < 0)) {
        e.revenue = 'Revenue must be a non-negative amount.';
      }
    }

    if (data.orderDate) {
      const d = new Date(data.orderDate);
      if (Number.isNaN(d.getTime())) {
        e.orderDate = 'Invalid date.';
      }
    }

    return e;
  };

  const validateAndSet = (next = formData, nextPigOpt = piggybackOption, nextPigName = piggybackOrderName) => {
    const e = buildErrors(next, nextPigOpt, nextPigName);
    setErrors(e);
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFieldTouched(name);
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      validateAndSet(next, piggybackOption, piggybackOrderName);
      return next;
    });
  };

  const handleDealerChange = (e) => {
    const selectedDealerId = e.target.value;
    setFieldTouched('dealerId');
    const selectedDealer = dealers.find(d => d.dealerId === selectedDealerId);
    setFormData(prev => {
      const next = {
        ...prev,
        dealerId: selectedDealerId,
        dealerName: selectedDealer ? selectedDealer.dealerName : ''
      };
      validateAndSet(next, piggybackOption, piggybackOrderName);
      return next;
    });
  };

  const handlePiggybackOptionChange = (opt) => {
    setPiggybackOption(opt);
    setFieldTouched('shippingMethod');
    validateAndSet(formData, opt, piggybackOrderName);
  };

  const handlePiggybackNameChange = (val) => {
    setPiggybackOrderName(val);
    setFieldTouched('piggybackOrderName');
    validateAndSet(formData, piggybackOption, val);
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setFieldTouched(name);
    validateAndSet();
  };

  // Check for duplicate invoice number
  const checkDuplicateInvoice = async (invoiceNumber) => {
    if (!invoiceNumber || invoiceNumber.trim() === '') {
      return false;
    }
    setModalMessage('');
    try {
      const res = await fetch(`${API_ROOT_URL}/orders/check-invoice/${encodeURIComponent(invoiceNumber)}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      return data.exists;
    } catch (err) {
      console.error('Error checking invoice number:', err);
      // If there's a network error, assume no duplicate for now and proceed.
      // The backend will still catch the error on submission.
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setModalMessage('');

    const touchAll = {
      shippingMethod: true,
      dealerId: true,
      customerName: true,
      orderDate: !!formData.orderDate
    };

    if (formData.invoiceNumber) {
      touchAll.invoiceNumber = true;
    }

    if (formData.tbcNumber) {
      touchAll.tbcNumber = true;
    }

    if (formData.shippingMethod === 'Piggyback' && piggybackOption === 'custom') {
      touchAll.piggybackOrderName = true;
    }

    if (!isParts) {
      Object.assign(touchAll, {
        frameType: true,
        length: true,
        width: true,
        height: true,
        upperColor: true,
        lowerColor: true,
        revenue: true
      });
    } else {
      if (formData.revenue !== '') touchAll.revenue = true;
    }

    setTouched(prev => ({ ...prev, ...touchAll }));
    const eObj = validateAndSet(formData, piggybackOption, piggybackOrderName);
    
    // Check for invoice number duplication *after* other validation
    if (formData.invoiceNumber && formData.invoiceNumber.trim() !== '') {
        const isDuplicate = await checkDuplicateInvoice(formData.invoiceNumber);
        if (isDuplicate) {
            eObj.invoiceNumber = 'This invoice number is already in use.';
        }
    }

    if (Object.keys(eObj).length > 0) {
      setErrors(eObj); // Set errors with the new invoice validation error
      setModalMessage('Please correct the highlighted fields.');
      setModalMessageType('danger');
      setLoading(false);
      return;
    }

    const dataToSend = {
      ...formData,
      length: formData.length === '' ? undefined : Number(formData.length),
      width: formData.width === '' ? undefined : Number(formData.width),
      height: formData.height === '' ? undefined : Number(formData.height),
      revenue: formData.revenue === '' ? undefined : Number(formData.revenue),
      orderDate: formData.orderDate ? new Date(formData.orderDate) : undefined,
      invoiceNumber: formData.invoiceNumber || undefined,
      tbcNumber: formData.tbcNumber || undefined
    };

    if (formData.shippingMethod === 'Piggyback') {
      if (piggybackOption === 'next') {
        dataToSend.shippingMethod = 'Piggyback with Next Cover';
      } else if (piggybackOrderName.trim() !== '') {
        dataToSend.shippingMethod = `Piggyback with ${piggybackOrderName.trim()}`;
      }
    }

    if (isParts) {
      delete dataToSend.frameType;
      delete dataToSend.length;
      delete dataToSend.width;
      delete dataToSend.height;
      delete dataToSend.upperColor;
      delete dataToSend.lowerColor;
      delete dataToSend.customWork;
      delete dataToSend.revenue;
      delete dataToSend.brackets;
    }

    try {
      const response = await fetch(`${API_ROOT_URL}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(dataToSend)
      });

      const result = await response.json();

      if (response.ok) {
        setSubmissionMessage(result.message || 'Order created successfully!', 'success');
        refreshOrders();
        onHide();
      } else {
        setModalMessage(result.message || 'Failed to create order.');
        setModalMessageType('danger');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      setModalMessage('Network error or server is unreachable.');
      setModalMessageType('danger');
    } finally {
      setLoading(false);
    }
  };

  const invalid = (name) => !!(touched[name] && errors[name]);
  const feedback = (name) => touched[name] && errors[name] ? errors[name] : '';

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add New Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {fetchError && <Alert variant="danger">{fetchError}</Alert>}
        {modalMessage && <Alert variant={modalMessageType}>{modalMessage}</Alert>}

        <Form onSubmit={handleSubmit} noValidate>
          {/* Order Type (read-only, gray) */}
          <Form.Group className="mb-3">
            <Form.Label>Order Type</Form.Label>
            <Form.Control
              type="text"
              value={formData.orderType}
              readOnly
              style={{ backgroundColor: '#e9ecef' }}
            />
          </Form.Group>

          {/* Shipping */}
          <Form.Group className="mb-3">
            <Form.Label>Shipping Method</Form.Label>
            <Form.Select
              name="shippingMethod"
              value={formData.shippingMethod}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={invalid('shippingMethod')}
            >
              <option value="" disabled>Select Shipping Method</option>
              {SHIPPING_METHODS.map(method => (
                <option key={method} value={method}>{method}</option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {feedback('shippingMethod')}
            </Form.Control.Feedback>
          </Form.Group>

          {formData.shippingMethod === 'Piggyback' && (
            <>
              <Form.Group className="mb-3">
                <Form.Check
                  type="radio"
                  label="Piggyback with Next Cover"
                  checked={piggybackOption === 'next'}
                  onChange={() => handlePiggybackOptionChange('next')}
                />
                <Form.Check
                  type="radio"
                  label="Piggyback with Specific Order"
                  checked={piggybackOption === 'custom'}
                  onChange={() => handlePiggybackOptionChange('custom')}
                />
              </Form.Group>
              {piggybackOption === 'custom' && (
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Enter Order Name"
                    value={piggybackOrderName}
                    onChange={(e) => handlePiggybackNameChange(e.target.value)}
                    onBlur={() => setFieldTouched('piggybackOrderName')}
                    isInvalid={invalid('piggybackOrderName')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {feedback('piggybackOrderName')}
                  </Form.Control.Feedback>
                </Form.Group>
              )}
            </>
          )}

          {/* Invoice Number */}
          <Form.Group className="mb-3">
            <Form.Label>Invoice Number (Optional)</Form.Label>
            <Form.Control
              name="invoiceNumber"
              value={formData.invoiceNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={invalid('invoiceNumber')}
              placeholder="Enter invoice number"
            />
            <Form.Control.Feedback type="invalid">
              {feedback('invoiceNumber')}
            </Form.Control.Feedback>
          </Form.Group>

          {/* TBC Number */}
          <Form.Group className="mb-3">
            <Form.Label>TBC Order Number (Optional)</Form.Label>
            <Form.Control
              name="tbcNumber"
              value={formData.tbcNumber}
              onChange={(e) => handleChange({ target: { name: 'tbcNumber', value: e.target.value.toUpperCase() } })}
              onBlur={handleBlur}
              isInvalid={invalid('tbcNumber')}
              placeholder="Enter TBC Order Number"
            />
            <Form.Control.Feedback type="invalid">
              {feedback('tbcNumber')}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Dealer */}
          <Form.Group className="mb-3">
            <Form.Label>Dealer Name</Form.Label>
            <Form.Select
              name="dealerId"
              value={formData.dealerId}
              onChange={handleDealerChange}
              onBlur={handleBlur}
              isInvalid={invalid('dealerId')}
            >
              <option value="">Select Dealer</option>
              {dealers.map(dealer => (
                <option key={dealer.dealerId} value={dealer.dealerId}>
                  {dealer.dealerName} ({dealer.dealerId})
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {feedback('dealerId')}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Customer */}
          <Form.Group className="mb-3">
            <Form.Label>Customer Name</Form.Label>
            <Form.Control
              name="customerName"
              value={formData.customerName}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={invalid('customerName')}
              placeholder="Enter customer's name"
            />
            <Form.Control.Feedback type="invalid">
              {feedback('customerName')}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Frame Type */}
          <Form.Group className="mb-3">
            <Form.Label>Frame Type</Form.Label>
            <Form.Select
              name="frameType"
              value={formData.frameType}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!isParts && invalid('frameType')}
              disabled={isParts}
            >
              <option value="">Select Frame Type</option>
              {FRAME_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Form.Select>
            {!isParts && (
              <Form.Control.Feedback type="invalid">
                {feedback('frameType')}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* Dimensions */}
          <Row className="mb-3">
            <Col>
              <Form.Control
                type="number"
                placeholder="Length"
                name="length"
                value={formData.length}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!isParts && invalid('length')}
                disabled={isParts}
                min="0"
                step="0.01"
              />
              {!isParts && (
                <Form.Control.Feedback type="invalid">
                  {feedback('length')}
                </Form.Control.Feedback>
              )}
            </Col>
            <Col>
              <Form.Control
                type="number"
                placeholder="Width"
                name="width"
                value={formData.width}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!isParts && invalid('width')}
                disabled={isParts}
                min="0"
                step="0.01"
              />
              {!isParts && (
                <Form.Control.Feedback type="invalid">
                  {feedback('width')}
                </Form.Control.Feedback>
              )}
            </Col>
            <Col>
              <Form.Control
                type="number"
                placeholder="Height"
                name="height"
                value={formData.height}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!isParts && invalid('height')}
                disabled={isParts}
                min="0"
                step="0.01"
              />
              {!isParts && (
                <Form.Control.Feedback type="invalid">
                  {feedback('height')}
                </Form.Control.Feedback>
              )}
            </Col>
          </Row>

          {/* Colors */}
          <Row className="mb-3">
            <Col>
              <Form.Select
                name="upperColor"
                value={formData.upperColor}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!isParts && invalid('upperColor')}
                disabled={isParts}
              >
                <option value="">Select Upper Color</option>
                {COLORS.map(color => <option key={color} value={color}>{color}</option>)}
              </Form.Select>
              {!isParts && (
                <Form.Control.Feedback type="invalid">
                  {feedback('upperColor')}
                </Form.Control.Feedback>
              )}
            </Col>
            <Col>
              <Form.Select
                name="lowerColor"
                value={formData.lowerColor}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!isParts && invalid('lowerColor')}
                disabled={isParts}
              >
                <option value="">Select Lower Color</option>
                {COLORS.map(color => <option key={color} value={color}>{color}</option>)}
              </Form.Select>
              {!isParts && (
                <Form.Control.Feedback type="invalid">
                  {feedback('lowerColor')}
                </Form.Control.Feedback>
              )}
            </Col>
          </Row>

          {/* Custom Work */}
          <Form.Group className="mb-3">
            <Form.Label>Custom Work</Form.Label>
            <Form.Control
              as="textarea"
              placeholder="Describe custom work"
              name="customWork"
              value={formData.customWork}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isParts /* original code sends customWork for non-Parts only */}
            />
          </Form.Group>

          {/* Brackets */}
          <Form.Group className="mb-3">
            <Form.Label>Brackets</Form.Label>
            <Form.Control
              type="text"
              name="brackets"
              value={formData.brackets}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g., Type A, custom brackets note"
              disabled={isParts}
            />
          </Form.Group>

          {/* Revenue */}
          <Form.Group className="mb-3">
            <Form.Label>Revenue</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              name="revenue"
              value={formData.revenue}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={(!isParts && invalid('revenue')) || (isParts && touched.revenue && errors.revenue)}
              disabled={isParts}
            />
            <Form.Control.Feedback type="invalid">
              {!isParts ? feedback('revenue') : (touched.revenue && errors.revenue ? errors.revenue : '')}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Order Date (Optional) */}
          <Form.Group className="mb-4">
            <Form.Label>Order Date</Form.Label>
            <Form.Control
              type="date"
              name="orderDate"
              value={formData.orderDate}
              onChange={handleChange}
              onBlur={handleBlur}
              className="w-auto"
              isInvalid={invalid('orderDate')}
            />
            <Form.Control.Feedback type="invalid">
              {feedback('orderDate')}
            </Form.Control.Feedback>
          </Form.Group>

          <Button variant="primary" type="submit" disabled={loading} className="w-100">
            {loading ? <Spinner animation="border" size="sm" /> : 'Create Order'}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default AddOrderModal;
